import DOMPurify from 'dompurify'

import {
   EMPTY_STRING,
   FLOATING_NUMBER_FIXED_DECIMAL_POINTS,
   SPACES_REGEX,
   SPECIAL_CHARS_REGEX,
   UNDERSCORE_STRING
} from '@flowwai-frontend/shared/constants'

export const changeGivenTextToUpperCase = (text: string) => text.toUpperCase()

export const getStringWithoutRegexSelectedCharacters = (
   text: string,
   regex: RegExp = SPECIAL_CHARS_REGEX
): string => text.replace(regex, '')
export const getFirstTwoLettersFromGivenName = (name: string) =>
   name.slice(0, 2)
export const getFirstLettersFromTheGivenStringList = (
   stringList: string[],
   stringsCountLimit = 2
) =>
   stringList
      .slice(0, stringsCountLimit)
      .map(stringValue => stringValue.charAt(0))
      .join(EMPTY_STRING)

export const getSecuredRandomNumber = (): number => {
   const arrayBuffer = new Uint32Array(1)

   window.crypto.getRandomValues(arrayBuffer)

   //NOTE: We are dividing the number with `2^32` to normalize the number range i.e [0, 1)
   const randomNumber = arrayBuffer[0] / 2 ** 32

   return randomNumber
}

export const getRandomIntInclusive = (min: number, max: number): number => {
   min = Math.ceil(min)
   max = Math.floor(max)

   const randomNumber = getSecuredRandomNumber()

   return Math.floor(randomNumber * (max - min + 1) + min)
}

export const getDomSanitizedValue = (value: string): string => {
   const sanitizedValue = DOMPurify.sanitize(value)

   return sanitizedValue
}

export const securedStringsCompare = (
   value1: string | number,
   value2: string | number
): boolean => {
   const stringifiedValue1 = value1.toString()
   const stringifiedValue2 = value2.toString()

   let result = 0

   const maxLength = Math.max(
      stringifiedValue1.length,
      stringifiedValue2.length
   )

   //NOTE:Performing constant-time comparison with Character Uni-Code

   for (let i = 0; i < maxLength; i++) {
      result |=
         (stringifiedValue1.charCodeAt(i % stringifiedValue1.length) || 0) ^
         (stringifiedValue2.charCodeAt(i % stringifiedValue2.length) || 0)
   }

   return result === 0 && stringifiedValue1.length === stringifiedValue2.length
}

export const replaceSpacesWithUnderScore = (value: string): string =>
   value.replace(SPACES_REGEX, UNDERSCORE_STRING)

export const getDisplayTextFromEnumString = (value: string): string =>
   value
      .toLowerCase()
      .split(/[_\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

export const getDisplayTextFromEnumStringWithFirstWordAsUpperCase = (
   value: string
): string =>
   value
      .toLowerCase()
      .split(/[_\s-]+/)
      .map((word, index) =>
         index !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(' ')

export const sanitizeFileName = (fileName: string | undefined | null) => {
   const hasFileName = fileName !== undefined || fileName !== null
   if (!hasFileName) return fileName

   // Remove path traversal characters and invalid chars
   return (
      fileName
         ?.replace(/[/\\?%*:|"<>]/g, '-')
         .replace(/\.\./g, '')
         .trim() ?? fileName
   )
}

export const getFloatFixedNumber = (
   number: number,
   decimalPoints = FLOATING_NUMBER_FIXED_DECIMAL_POINTS
): number => Number(number.toFixed(decimalPoints))

export const checkIsNumber = (value: string): boolean => /\d+/g.test(value)
